import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastService } from '../../ui';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { SNACKBAR_ERROR_CONFIG } from '../../shared/utils/notification-defaults';
import { CcNumbersService } from './cc-numbers.service';
import { PhoneNumberType, RegulationAddressDetails, RegulationInformation } from './cc-numbers.model';
import {
  addressCheckAppliesTo, addressCheckBlockReason, AddressCheckComponent, addressCheckDisablesSubmit,
  AddressCheckState, isAddressCheckBlocking,
} from './address-check/address-check.component';
import { RegulationAddressSnapshot, RegulationFormComponent } from './regulation-form/regulation-form.component';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
} from '../../ui';

export interface FixRegulationDialogData {
  phoneServiceId: number;
  number: string;
  numberType: PhoneNumberType;
  /** Why the regulation was rejected — shown as a banner so the fix dialog is self-explanatory. */
  rejectionReasons?: string[] | null;
  /** ONKZ the number belongs to (PhoneNumber.numberBlock.cityPrefix). Without it the address check
   *  cannot compare anything and stays off — a guessed prefix would produce false mismatches. */
  cityPrefix?: string | null;
}

/**
 * Statuses returned by the fix PUT that mean the re-submission was accepted (verification kicked off
 * or not needed). Anything else (REJECTED / FAILED / unknown) is surfaced inline and the dialog stays
 * open so the operator can correct and retry.
 */
const SUCCESS_STATUSES = [
  'VERIFICATION_REQUESTED',
  'VERIFICATION_NOT_REQUIRED',
  'NUMBERS_LINKED_AND_UNBLOCKED',
  'SUCCESS',
  'VERIFIED',
];

/**
 * "Fix regulation" dialog: loads the prefill (field requirements + current rejected values) for a
 * failed/rejected number, hosts the shared RegulationFormComponent so the operator can edit address
 * + identity data and re-upload proofs, then re-submits via the multipart PUT. On an accepted status
 * it toasts + closes(true) so the parent reloads; on REJECTED/FAILED it keeps the dialog open and
 * shows the returned status inline.
 */
@Component({
  selector: 'cmt-fix-regulation-dialog',
  standalone: true,
  imports: [
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent,
    RegulationFormComponent,
    AddressCheckComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-dialog-surface size="lg">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideWrench" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle class="font-mono-feat">{{ data.number }}</h2>
          <p hlmDialogDescription>Repair the regulation address and re-submit for verification</p>
        </div>
        <button hlmDialogClose tabindex="-1" (click)="dialogRef.close(false)">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="max-h-[calc(100vh-120px)] overflow-y-auto">
        @if (loading()) {
          <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
            <hlm-spinner size="20" /> Loading regulation data…
          </div>
        } @else if (loadError(); as err) {
          <div class="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <hlm-icon name="lucideCircleAlert" size="0.9rem" />
            <span>{{ err }}</span>
          </div>
        } @else {
          @if (data.rejectionReasons?.length) {
            <div class="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
              <div class="flex items-center gap-2 text-sm font-semibold text-destructive">
                <hlm-icon name="lucideCircleX" size="0.9rem" />
                Regulation rejected — correct the data below and re-submit
              </div>
              <ul class="mt-2 list-disc space-y-1 pl-7 text-sm text-destructive">
                @for (reason of data.rejectionReasons; track reason) {
                  <li>{{ reason }}</li>
                }
              </ul>
            </div>
          }
          <cmt-regulation-form
            [requirements]="requirements()"
            [prefill]="prefill()"
            [numberType]="data.numberType"
            (addressValueChange)="addressSnapshot.set($event)"
          >
            <cmt-address-check addressCheck
              [isoCountryCode]="isoCountryCode()"
              [cityPrefix]="data.cityPrefix ?? ''"
              [address]="addressSnapshot()"
              [enabled]="addressCheckEnabled()"
              (stateChange)="addressCheckState.set($event)"
              (correctionApplied)="regulationForm?.applyAddressCorrection($event)"
            />
          </cmt-regulation-form>
          @if (submitStatus(); as status) {
            <div class="mt-3 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <hlm-icon name="lucideCircleX" size="0.9rem" />
              <span>Re-submission was not accepted (status: {{ humanize(status) }}). Correct the data and try again.</span>
            </div>
          }
        }
      </section>

      <footer hlmDialogFooter>
        @if (submitDisabled() && blockReason(); as reason) {
          <!-- Only for a truly disabled Submit (§17.2). Far left, BEFORE Cancel: the Cancel + Submit pair
               keeps its right-aligned position whether or not a hint is shown (Style Guide §8). -->
          <span class="mr-auto text-sm text-foreground" data-testid="fix-regulation-block-reason">{{ reason }}</span>
        }
        <button hlmBtn variant="outline" type="button" (click)="dialogRef.close(false)">Cancel</button>
        <button hlmBtn type="button" (click)="submit()" [disabled]="submitDisabled()" data-testid="fix-regulation-submit">
          <hlm-icon name="lucideWrench" size="0.9rem" />
          {{ submitting() ? 'Submitting…' : 'Submit' }}
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class FixRegulationDialogComponent {
  readonly dialogRef = inject(MatDialogRef<FixRegulationDialogComponent>);
  readonly data: FixRegulationDialogData = inject(MAT_DIALOG_DATA);
  private readonly api = inject(CcNumbersService);
  private readonly toast = inject(ToastService);

  @ViewChild(RegulationFormComponent) regulationForm?: RegulationFormComponent;

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly submitting = signal(false);
  /** Last non-success status returned by the fix PUT — shown inline; null while none. */
  readonly submitStatus = signal<string | null>(null);

  readonly requirements = signal<RegulationInformation | null>(null);
  readonly prefill = signal<RegulationAddressDetails | null>(null);

  constructor() {
    this.loadPrefill();
  }

  private loadPrefill(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.api.getRegulationPrefill(this.data.phoneServiceId, this.data.number).subscribe({
      next: ({ requirements, values }) => {
        this.requirements.set(requirements);
        this.prefill.set(values);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set('Could not load the regulation data for this number. Please try again later.');
      },
    });
  }

  // --- Address check (PS-6489) ---

  readonly addressSnapshot = signal<RegulationAddressSnapshot | null>(null);
  readonly addressCheckState = signal<AddressCheckState | null>(null);

  /** Country of the regulation address being repaired, as loaded with the prefill. */
  readonly isoCountryCode = computed(() => this.prefill()?.countryCode ?? '');

  /** Same scope guard as the booking dialog, plus the number's own ONKZ: without a city prefix
   *  there is nothing to compare the geocoded area code against. */
  readonly addressCheckEnabled = computed(() => {
    if (!this.data.cityPrefix) return false;
    if (this.isoCountryCode() !== 'DEU') return false;
    return addressCheckAppliesTo(this.requirements()?.addressRequirements);
  });

  /** Submit is gated on the hosted form being valid (it owns all validation) and on the address
   *  check — a repaired address must not be re-submitted into the same rejection it came from. */
  canSubmit(): boolean {
    if (this.loading() || this.loadError()) return false;
    if (!(this.regulationForm?.isValid() ?? false)) return false;
    return !this.addressCheckBlocked();
  }

  /** Same rule as the booking dialog — both delegate, so the two gates cannot drift apart. */
  private addressCheckBlocked(): boolean {
    if (!this.addressCheckEnabled()) return false;
    return isAddressCheckBlocking(this.addressCheckState());
  }

  /**
   * Submit stays clickable on an incomplete form (submit-to-reveal, as in the booking dialog);
   * disabled only while loading, on a load error, while submitting, or while the address check
   * runs / holds a blocking verdict (shared rule `addressCheckDisablesSubmit`).
   * @ticket EB-3809
   */
  submitDisabled(): boolean {
    if (this.loading() || this.loadError() || this.submitting()) return true;
    return this.addressCheckEnabled() && addressCheckDisablesSubmit(this.addressCheckState());
  }

  /**
   * Why Submit is disabled by the address check — rendered only while `submitDisabled()`.
   * Same shared helper as the booking dialog; only the action word differs ("re-submit").
   * Loading/submitting explain themselves (spinner, "Submitting…").
   * @ticket EB-3809
   */
  blockReason(): string | null {
    if (this.loading() || this.loadError() || !this.addressCheckEnabled()) return null;
    return addressCheckBlockReason(this.addressCheckState(), 're-submit');
  }

  humanize(status: string): string {
    return status.replace(/_/g, ' ');
  }

  submit(): void {
    // Re-checked here and not only via the button's [disabled], mirroring the booking dialog: the
    // address gate must not depend on the template alone.
    if (this.submitting()) return;
    if (!this.canSubmit()) {
      // Submit-to-reveal: show what is missing instead of doing nothing. (An address-check block
      // disables the button instead — see submitDisabled.)
      if (this.regulationForm && !this.regulationForm.isValid()) this.regulationForm.revealMissing();
      return;
    }
    const value = this.regulationForm?.getValue();
    if (!value) return;
    this.submitting.set(true);
    this.submitStatus.set(null);
    this.api.fixRegulationAddress(this.data.phoneServiceId, this.data.number, value).subscribe({
      next: (result) => {
        this.submitting.set(false);
        if (SUCCESS_STATUSES.includes(result.status)) {
          this.toast.open('Regulation re-submitted', 'OK', { duration: 3000 });
          this.dialogRef.close(true);
        } else {
          // REJECTED / FAILED / unknown — keep the dialog open, surface the status inline.
          this.submitStatus.set(result.status);
        }
      },
      error: (err) => {
        this.submitting.set(false);
        this.toast.open(extractErrorMessage(err, 'Failed to re-submit the regulation'), 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }
}
